import { DataType, IDataset, IDatasetControl, isDatasetNode, isJsonObject, JsonObject } from '@se/cp.common';
import { cloneDeep } from 'lodash-es';

export function deepClone<T>(obj: T): T {
    if (typeof structuredClone === 'function') {
        return structuredClone(obj);
    }
    return cloneDeep(obj);
}

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

function toText(v: any): string | null {
    if (v === undefined || v === null) return null;
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return `${v}`;
    // evita oggetti => "[object Object]" (di solito indesiderato)
    return null;
}

function toFiniteNumber(v: any): number | null {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return null;
        // parse in modo esplicito (Number() sotto fa la stessa cosa, ma qui è più "controllato")
        const n = +s;
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

function toBooleanSoft(v: any): boolean | null {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (['true', '1', 'yes', 'y'].includes(s)) return true;
        if (['false', '0', 'no', 'n'].includes(s)) return false;
    }
    return null;
}

function toIsoDateTime(v: any): string | null {
    if (typeof v !== 'string') return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
}

function toGuidSoft(v: any): string | null {
    if (typeof v !== 'string') return null;
    return GUID_RE.test(v) ? v : null;
}

function toGuidArraySoft(v: any): string[] | null {
    if (Array.isArray(v)) {
        const out: string[] = [];
        for (const item of v) {
            if (typeof item !== 'string' || !GUID_RE.test(item)) return null;
            out.push(item);
        }
        return out;
    }
    if (typeof v === 'string') {
        const parts = v
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean);
        for (const p of parts) if (!GUID_RE.test(p)) return null;
        return parts;
    }
    return null;
}

/**
 * Normalizza un valore in base al DataType del dataset.
 * - Non lancia errori
 * - Ritorna null se il valore non è valido
 * - Il caller decide eventuale fallback (default/null)
 */

function normalizeValueByDataType(dataType: DataType, v: any): any {
    if (v === undefined) return undefined;
    if (v === null) return null;

    switch (dataType) {
        case 'String':
        case 'FormattedString':
        case 'ImageId': {
            const s = toText(v);
            if (s === null) console.warn(`${dataType} non valido:`, v);
            return s;
        }

        case 'Boolean': {
            const b = toBooleanSoft(v);
            if (b === null) console.warn(`Boolean non valido:`, v);
            return b;
        }

        case 'Number': {
            const n = toFiniteNumber(v);
            if (n === null) console.warn(`Number non valido:`, v);
            return n;
        }

        case 'Integer': {
            const n = toFiniteNumber(v);
            if (n === null || !Number.isInteger(n)) {
                console.warn(`Integer non valido:`, v);
                return null;
            }
            return n;
        }

        case 'DateTime': {
            const iso = toIsoDateTime(v);
            if (iso === null) console.warn(`DateTime non valido:`, v);
            return iso;
        }

        case 'Guid': {
            const g = toGuidSoft(v);
            if (g === null) console.warn(`Guid non valido:`, v);
            return g;
        }

        case 'GuidArray': {
            const arr = toGuidArraySoft(v);
            if (arr === null) console.warn(`GuidArray non valido:`, v);
            return arr;
        }

        default:
            return v;
    }
}

function formatChildPath(base: string, childKey: string | number): string {
    if (base === '') return `${childKey}`;
    return `${base}.${childKey}`;
}

function dfsFind<T>(node: any, path: string, onMatch: (node: any, path: string) => T | undefined): T | undefined {
    if (node == null) return undefined;

    const matched = onMatch(node, path);
    if (matched !== undefined) return matched;

    if (Array.isArray(node)) {
        for (let i = 0; i < node.length; i++) {
            const childPath = formatChildPath(path, i);
            const found = dfsFind(node[i], childPath, onMatch);
            if (found !== undefined) return found;
        }
        return undefined;
    }

    if (isJsonObject(node)) {
        for (const k of Object.keys(node)) {
            const childPath = formatChildPath(path, k);
            const found = dfsFind(node[k], childPath, onMatch);
            if (found !== undefined) return found;
        }
    }

    return undefined;
}

function isIndexSegment(seg: string): boolean {
    // "0", "1", "23" => array index
    return /^[0-9]+$/.test(seg);
}

function splitPath(path: string): string[] {
    return path.split('.').filter(Boolean);
}

function cloneContainer(x: any, wantArray: boolean) {
    if (wantArray) return Array.isArray(x) ? x.slice() : [];
    return isJsonObject(x) ? { ...x } : {};
}

/** trasforma il model path (con indici) nel dataset path (senza indici) */
export function modelPathToDatasetPath(modelPath: string): string {
    return modelPath
        .split('.')
        .filter(Boolean)
        .filter((seg) => !isIndexSegment(seg))
        .join('.');
}

/** 1) Legge il nodo a path del modello runtime.*/
export const getNodeAtPath = (obj: unknown, path: string): unknown => {
    const keys = path.split('.').filter(Boolean);
    let cur: any = obj;

    for (const k of keys) {
        if (cur == null || typeof cur !== 'object') return undefined;
        cur = cur[k];
    }
    return cur;
};

/** 2) Legge il nodo a path di IDataset. */
export const getDatasetNodeAtPath = (obj: IDataset, path: string): IDataset | IDatasetControl | undefined => {
    const keys = path.split('.').filter(Boolean);
    let cur: IDataset | IDatasetControl = obj;

    for (const k of keys) {
        if (cur == null) return undefined;
        if (!isDatasetNode(cur)) return cur;
        const control = cur.controls.find((c) => c.name === k);
        if (control) return control;
        const child: IDataset | undefined =
            cur.childrenArray.find((c) => c.name === k) ?? cur.childrenObject.find((c) => c.name === k);
        if (!child) return undefined;
        cur = child;
    }
    return cur;
};

export const createValidatedObject = (datasetNode: IDataset, values: Record<string, any>): JsonObject => {
    const obj: JsonObject = {};
    datasetNode.controls.forEach((control) => {
        // valore invalido o assente => default (se c'è) altrimenti null
        const normalizedValue = normalizeValueByDataType(control.dataType, values[control.name]);
        const valueToSet = normalizedValue ?? control.defaultValue ?? null;
        if (valueToSet === null && !control.isNullable) return;
        obj[control.name] = valueToSet;
    });
    datasetNode.childrenArray.forEach((child) => (obj[child.name] = []));
    return obj;
};

export const createValidatedObjectAtPath = (
    datasetRoot: IDataset,
    path: string,
    values: Record<string, any>,
): JsonObject | undefined => {
    const datasetPath = modelPathToDatasetPath(path);
    const datasetNode = getDatasetNodeAtPath(datasetRoot, datasetPath);
    if (!datasetNode) {
        console.warn(`Dataset path not found: ${datasetPath}`);
        return undefined;
    }
    if (!isDatasetNode(datasetNode)) {
        console.error(`Dataset path is not valid (not a IDataset node): ${datasetPath}`);
        return undefined;
    }
    return createValidatedObject(datasetNode, values);
};

export const normalizeLeafValueByPath = (datasetRoot: IDataset, path: string, rawValue: unknown): unknown => {
    const datasetPath = modelPathToDatasetPath(path);
    const datasetNode = getDatasetNodeAtPath(datasetRoot, datasetPath);

    if (!datasetNode) {
        console.error(`Dataset path not found: ${datasetPath}`);
        return undefined;
    }

    // Deve essere una foglia (IDatasetControl), quindi NON deve essere un IDataset
    if (isDatasetNode(datasetNode)) {
        console.error(`Dataset path is not a leaf (control): ${datasetPath}`);
        return undefined;
    }

    const control = datasetNode;
    const normalized = normalizeValueByDataType(control.dataType, rawValue);
    const valueToSet = normalized ?? control.defaultValue ?? null;

    if (valueToSet === null && !control.isNullable) {
        console.error(`Field is not nullable: ${datasetPath}`);
        return undefined;
    }
    return valueToSet;
};

export const normalizePatchByPath = (
    datasetRoot: IDataset,
    path: string, // può avere indici (es: "items.0")
    patch: Record<string, unknown>,
): JsonObject | undefined => {
    const datasetPath = modelPathToDatasetPath(path);
    const datasetNode = getDatasetNodeAtPath(datasetRoot, datasetPath);

    if (!datasetNode) {
        console.error(`Dataset path not found: ${datasetPath}`);
        return undefined;
    }

    if (!isDatasetNode(datasetNode)) {
        console.warn(`Dataset path is not an object node: ${datasetPath}`);
        return undefined;
    }
    const out: JsonObject = {};

    for (const [key, rawValue] of Object.entries(patch)) {
        // policy merge: undefined non sovrascrive
        if (rawValue === undefined) continue;

        const control = datasetNode.controls.find((c) => c.name === key);
        if (!control) {
            // chiave non prevista dal dataset => ignoro
            continue;
        }

        const normalized = normalizeValueByDataType(control.dataType, rawValue);
        const valueToSet = normalized ?? control.defaultValue ?? null;

        if (valueToSet === null && !control.isNullable) {
            console.error(`Field is not nullable, skipped: ${datasetPath}.${key}`);
            continue;
        }

        out[key] = valueToSet;
    }

    return out;
};

export const findObjectByKeyValue = (root: unknown, key: string, value: unknown) =>
    dfsFind(root, '', (node) =>
        isJsonObject(node) && Object.prototype.hasOwnProperty.call(node, key) && node[key] === value ? node : undefined,
    );

export const findPathByKeyValue = (root: unknown, key: string, value: unknown) =>
    dfsFind(root, '', (node, path) =>
        isJsonObject(node) && Object.prototype.hasOwnProperty.call(node, key) && node[key] === value ? path : undefined,
    );

/**
 * Core immutabile: setta un valore a path.
 * - Path in formato "a.b.0.c"
 * - Crea contenitori mancanti
 * - Preserva structural sharing
 */
export function setAtPathImmutable<T extends JsonObject>(model: T, path: string, value: any): T {
    const parts = splitPath(path);
    if (parts.length === 0) return model;

    const newRoot: any = cloneContainer(model, false);

    let curNew: any = newRoot;
    let curOld: any = model;

    for (let i = 0; i < parts.length - 1; i++) {
        const seg = parts[i];
        const nextSeg = parts[i + 1];
        const nextWantsArray = isIndexSegment(nextSeg);

        const key: any = isIndexSegment(seg) ? Number(seg) : seg;

        const oldChild = curOld?.[key];
        const newChild = cloneContainer(oldChild, nextWantsArray);

        curNew[key] = newChild;

        curNew = newChild;
        curOld = oldChild;
    }

    const lastSeg = parts[parts.length - 1];
    const lastKey: any = isIndexSegment(lastSeg) ? Number(lastSeg) : lastSeg;
    curNew[lastKey] = value;

    return newRoot as T;
}

/** 3) Mettere un oggetto in un dato path (sostituisce/crea quel nodo) */
export const setObjectAtPath = <T extends JsonObject>(model: T, path: string, obj: JsonObject): T =>
    setAtPathImmutable(model, path, obj);

/**
 * 4) Inserire un oggetto in un array a path (push).
 * Se a path non c'è un array, lo crea e inserisce il primo elemento.
 */
export function pushObjectAtArrayPath<T extends JsonObject>(model: T, arrayPath: string, obj: JsonObject): T {
    // leggo in modo semplice senza dipendenze (usa il tuo getNodeAtPath se vuoi)
    const existing = getNodeAtPath(model, arrayPath);
    const nextArr = Array.isArray(existing) ? [...existing, obj] : [obj];
    return setAtPathImmutable(model, arrayPath, nextArr);
}

export const setArrayItemAtPath = <T extends JsonObject>(
    model: T,
    arrayPath: string,
    index: number,
    obj: JsonObject,
): T => setAtPathImmutable(model, `${arrayPath}.${index}`, obj);

/** 5) Mettere un valore in una foglia (string/number/boolean/null/array/object) */
export const setLeafValueAtPath = <T extends JsonObject>(model: T, path: string, value: any): T =>
    setAtPathImmutable(model, path, value);

export const mergeObjectAtPath = <T extends JsonObject>(model: T, path: string, patch: Partial<JsonObject>): T => {
    const existing = getNodeAtPath(model, path);

    if (isJsonObject(existing)) {
        const merged = { ...existing };

        for (const key of Object.keys(patch)) {
            const v = (patch as any)[key];
            if (v !== undefined) {
                merged[key] = v;
            }
        }

        return setAtPathImmutable(model, path, merged);
    }

    // se non esiste o non è object → sostituisce
    return setAtPathImmutable(model, path, { ...patch });
};
